'use client';

import { useAddressAutocomplete, useBusinessInfo } from '@ctct/data-query';
import { getCoordinatesFromCookies, LocationUtils } from '@ctct/rise-utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

const DEFAULT_SEARCH_RADIUS = 80468; // ~50 miles
const MIN_SEARCH_LENGTH = 3;

interface UseAddressLookupOptions {
  isEnabled?: boolean;
  formKeyPrefix?: string;
  lat?: number;
  long?: number;
  radius?: number;
}

const DEFAULT_ADDRES_LOOKUP_VALUES = {
  isEnabled: true,
  radius: DEFAULT_SEARCH_RADIUS,
};
const useAddressLookup = ({
  isEnabled = true,
  formKeyPrefix,
  lat: latOverride,
  long: longOverride,
  radius = DEFAULT_SEARCH_RADIUS,
}: UseAddressLookupOptions = DEFAULT_ADDRES_LOOKUP_VALUES) => {
  const countryCode = useWatch({
    name: formKeyPrefix ? `${formKeyPrefix}.countryCode` : 'countryCode',
  }) as string | undefined;
  const countryName =
    LocationUtils.countries.find(
      (country) => country.abbreviation === countryCode?.trim().toLowerCase()
    )?.name ?? countryCode?.trim();
  const cookieCoords = useMemo(() => getCoordinatesFromCookies(), []);
  const lat = latOverride ?? cookieCoords?.lat ?? 0;
  const long = longOverride ?? cookieCoords?.long ?? 0;
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleSelectPlaceId = useCallback((placeId: string) => {
    setSelectedPlaceId(placeId);
  }, []);

  const shouldFetch = isEnabled && searchQuery.length >= MIN_SEARCH_LENGTH;

  const { data: autocompleteData, isLoading: isLoadingAutocomplete } = useAddressAutocomplete(
    {
      criteria: [searchQuery, countryName].filter(Boolean).join(', '),
      lat,
      long,
      radius,
    },
    {
      enabled: shouldFetch,
      staleTime: 5 * 60 * 1000,
      retry: 1,
    }
  );

  const { data: businessInfoData, isLoading: isLoadingBusinessInfo } = useBusinessInfo(
    { placeId: selectedPlaceId ?? '' },
    {
      enabled: !!selectedPlaceId,
      retry: 1,
    }
  );

  useEffect(() => {
    if (businessInfoData) setSelectedPlaceId(null);
  }, [businessInfoData]);

  if (!isEnabled) return undefined;

  return {
    formKeyPrefix,
    autocompleteData,
    isLoadingAutocomplete,
    businessInfoData,
    isLoadingBusinessInfo,
    onSelectPlaceId: handleSelectPlaceId,
    onSearchChange: handleSearchChange,
  };
};

export { useAddressLookup, type UseAddressLookupOptions };
